import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from './ui/dropdown-menu';
import OnboardingTour, { useOnboarding } from './OnboardingTour';
import NotificationBell from './NotificationBell';
import TranscriptReadyPill from './TranscriptReadyPill';
import QuickEstimateDrawer from './QuickEstimateDrawer';
import GlobalCommandPalette from './GlobalCommandPalette';
import PaletteCallout from './PaletteCallout';
import logger from '../lib/logger';
import useBuildVersionWatcher from '../hooks/useBuildVersionWatcher';
import {
  Calculator, Clock, Settings, Users, Building2,
  ChevronDown, LogOut, User, Bell, Shield, ShieldCheck, GitCompareArrows, CalendarDays, BarChart3, Menu,
  MapPin, BookOpen, Briefcase, FolderOpen, Home, Zap, Eye, X, Wand2, HelpCircle, Plus, Layers, UserCircle2, Gauge, GraduationCap, BookMarked, BookOpenText
} from 'lucide-react';

const ALL_ROLES = ['user', 'admin', 'cs', 'cro', 'cco', 'super_admin'];
const ADMIN_ROLES = ['admin', 'cs', 'cro', 'cco', 'super_admin'];

const ROLE_LABELS = {
  super_admin: { label: 'Super Admin', bg: 'bg-purple-100', text: 'text-purple-700' },
  cco: { label: 'CCO', bg: 'bg-rose-100', text: 'text-rose-700' },
  cro: { label: 'CRO', bg: 'bg-amber-100', text: 'text-amber-700' },
  admin: { label: 'Sales', bg: 'bg-blue-100', text: 'text-blue-700' },
  cs: { label: 'CS', bg: 'bg-teal-100', text: 'text-teal-700' },
  user: { label: 'User', bg: 'bg-slate-100', text: 'text-slate-600' },
};

// Primary nav restructure (Feb 2026 UX pass) — three semantic clusters:
//   • Library  → where you start (Workspaces is now the home for signed-in users)
//   • Workflow → the build chain (Estimator → Compare → Planner → Simulator)
//   • Artifacts → the outputs you share (Quote, Insights)
// History is no longer a nav item — `/history` redirects to `/workspaces`.
// Admin is exposed as a single button that routes to /admin (which has its
// own in-page sidebar — no duplicate dropdown).
const NAV_GROUPS = [
  {
    id: 'workspaces', direct: true,
    items: [{ path: '/workspaces', label: 'Workspaces', icon: FolderOpen, roles: ALL_ROLES }],
  },
  {
    id: 'estimates', label: 'Estimates', icon: Layers,
    items: [
      { path: '/my-estimates', label: 'My Estimates', icon: UserCircle2, roles: ADMIN_ROLES },
      { path: '/workspace-overview', label: 'Portfolio', icon: Layers, roles: ADMIN_ROLES },
      { path: '/team-availability', label: 'Resource Planning', icon: CalendarDays, roles: ADMIN_ROLES },
    ],
  },
  {
    id: 'build', label: 'Build', icon: Calculator,
    items: [
      { path: '/estimator', label: 'Estimator', icon: Calculator, roles: ADMIN_ROLES },
      { path: '/compare', label: 'Compare', icon: GitCompareArrows, roles: ADMIN_ROLES },
      { path: '/planner', label: 'Planner', icon: CalendarDays, roles: ADMIN_ROLES },
      { path: '/simulator', label: 'Simulator', icon: Wand2, roles: ADMIN_ROLES },
    ],
  },
  {
    id: 'deals', label: 'Deals', icon: Briefcase,
    items: [
      { path: '/quotes', label: 'Quote', icon: Briefcase, roles: ADMIN_ROLES },
      { path: '/rfp-library', label: 'Capability Library', icon: BookMarked, roles: ADMIN_ROLES },
      { path: '/rfp-narrative-library', label: 'Narrative Library', icon: BookOpenText, roles: ADMIN_ROLES },
      { path: '/rfp-response', label: 'RFP Response', icon: BookMarked, roles: ADMIN_ROLES },
    ],
  },
  {
    id: 'analytics', label: 'Analytics', icon: BarChart3,
    items: [
      { path: '/insights', label: 'Insights', icon: BarChart3, roles: ADMIN_ROLES },
      { path: '/maturity', label: 'Maturity', icon: Gauge, roles: ADMIN_ROLES },
      { path: '/verification-health', label: 'Health', icon: ShieldCheck, roles: ADMIN_ROLES },
    ],
  },
];

// Help dropdown — Math Reference + Take a Tour live here so the primary
// nav stays focused on the workflow.
const HELP_ITEMS = [
  { path: '/user-guide', label: 'User Guide', icon: GraduationCap },
  { path: '/math-reference', label: 'Math Reference', icon: BookOpen },
];

function UserAvatar({ user, size = 'sm' }) {
  const sizeClasses = size === 'sm' ? 'w-9 h-9 text-xs' : 'w-12 h-12 text-sm';
  
  if (user?.picture) {
    return (
      <img
        src={user.picture}
        alt={user.name}
        className={`${sizeClasses} rounded-full object-cover border-2 border-white shadow-sm`}
        referrerPolicy="no-referrer"
      />
    );
  }

  const initials = (user?.name || 'U')
    .split(' ')
    .map(w => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  return (
    <div className={`${sizeClasses} rounded-full bg-[#0F2040] text-white flex items-center justify-center font-semibold border-2 border-white shadow-sm`}>
      {initials}
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout, impersonation, stopImpersonating, trackImpersonationPage } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [importPending, setImportPending] = useState(0);

  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) { setImportPending(0); return; }
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/rfp/import/pending-count`, { withCredentials: true })
      .then(res => setImportPending(res.data?.pending || 0))
      .catch(() => {});
  }, [user, location.pathname]);
  const [profileOpen, setProfileOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [quickDrawerOpen, setQuickDrawerOpen] = useState(false);
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const profileRef = useRef(null);
  const helpRef = useRef(null);
  const { active: tourActive, complete: tourComplete, restart: tourRestart } = useOnboarding();

  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const isOnAdminPage = location.pathname === '/admin' || location.pathname === '/settings';
  const isOnHelpPage = HELP_ITEMS.some(r => location.pathname === r.path);

  // Watch for backend SHA drift while this tab stays open.
  useBuildVersionWatcher();

  const handleStopImpersonating = async () => {
    setStoppingImpersonation(true);
    try {
      await stopImpersonating();
      navigate('/admin?tab=users');
    } catch (err) {
      logger.error('Stop impersonation error:', err);
    } finally {
      setStoppingImpersonation(false);
    }
  };

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
      if (helpRef.current && !helpRef.current.contains(e.target)) setHelpOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Track page visits during impersonation
  useEffect(() => {
    if (impersonation) {
      trackImpersonationPage(location.pathname + location.search);
    }
  }, [location.pathname, location.search, impersonation, trackImpersonationPage]);

  const handleLogout = async () => {
    setProfileOpen(false);
    await logout();
    navigate('/');
  };

  const navTo = (path) => {
    setProfileOpen(false);
    setHelpOpen(false);
    navigate(path);
  };

  // Click handler for the "+ New Estimate" CTA — sends the user to the
  // Conditions Flow wizard so every entry-point converges on the same
  // unidirectional flow (matches WorkspaceDashboardPage behavior).
  const handleNewEstimate = () => navTo('/estimator/new');

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ─── IMPERSONATION BANNER ─────────────────────────────────── */}
      {impersonation && (
        <div className="bg-amber-500 text-white sticky top-0 z-[60]" data-testid="impersonation-banner">
          <div className="w-full px-4 sm:px-6 lg:px-8 h-10 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Eye className="w-4 h-4" />
              <span>Viewing as <strong>{impersonation.target.name}</strong> ({impersonation.target.email})</span>
              <span className="px-1.5 py-0.5 bg-white/20 rounded text-xs">{(ROLE_LABELS[impersonation.target.role] || ROLE_LABELS.user).label}</span>
              {impersonation.target.organization_name && (
                <span className="px-1.5 py-0.5 bg-white/20 rounded text-xs" data-testid="impersonation-org-chip">
                  {impersonation.target.organization_name}
                </span>
              )}
            </div>
            <button
              onClick={handleStopImpersonating}
              disabled={stoppingImpersonation}
              className="flex items-center gap-1.5 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-md text-sm font-medium transition-colors"
              data-testid="stop-impersonation-btn"
            >
              <X className="w-3.5 h-3.5" />
              {stoppingImpersonation ? 'Restoring...' : 'Stop Impersonating'}
            </button>
          </div>
        </div>
      )}

      {/* ─── HEADER ──────────────────────────────────────────────── */}
      <header className={`bg-white border-b border-slate-200 sticky ${impersonation ? 'top-10' : 'top-0'} z-50`} data-testid="app-header">

        {/* Row 1: Brand + Global actions */}
        <div className="w-full px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">

          {/* Left: Seertech infinity mark + "Blueprint Studio" wordmark */}
          <button
            onClick={() => navTo(user ? '/workspaces' : '/')}
            className="flex items-center gap-2.5 hover:opacity-80 transition-opacity"
            data-testid="home-btn"
          >
            <img
              src="/seertech-mark.png"
              alt="Seertech"
              className="h-8 w-8 object-contain shrink-0"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <span className="text-base font-bold text-[#0F2040] hidden sm:inline tracking-tight">Blueprint Studio</span>
          </button>

          {/* Right: New Estimate CTA + Quick Estimate + Help + Notifications + Profile */}
          <div className="flex items-center gap-2">
            {isAdmin && (
              <>
            <button
              onClick={handleNewEstimate}
              className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-[#4ABFCF] text-white hover:bg-[#3aa9b8] transition-colors shadow-sm"
              data-testid="header-new-estimate-btn"
              title="Start a new estimate"
            >
              <Plus className="w-3.5 h-3.5" />
              New Estimate
            </button>
            <button
              onClick={() => setQuickDrawerOpen(true)}
              className="p-1.5 rounded-md hover:bg-[#4ABFCF]/10 transition-colors text-slate-400 hover:text-[#4ABFCF]"
              title="Quick Estimate"
              data-testid="quick-estimate-trigger"
            >
              <Zap className="w-4 h-4" />
            </button>
              </>
            )}
            {/* Help dropdown — Math Reference + Tour live here so primary
                nav stays workflow-focused. */}
            <div className="relative" ref={helpRef}>
              <button
                onClick={() => setHelpOpen(!helpOpen)}
                className={`p-1.5 rounded-md transition-colors ${
                  isOnHelpPage || helpOpen
                    ? 'bg-slate-100 text-[#0F2040]'
                    : 'text-slate-400 hover:text-[#0F2040] hover:bg-slate-50'
                }`}
                title="Help & references"
                data-testid="help-trigger"
              >
                <HelpCircle className="w-4 h-4" />
              </button>
              {helpOpen && (
                <div className="absolute top-full right-0 mt-2 w-52 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50" data-testid="help-dropdown">
                  {HELP_ITEMS.map(item => (
                    <button
                      key={item.path}
                      onClick={() => navTo(item.path)}
                      className={`w-full flex items-center gap-2.5 px-3.5 py-2 text-xs transition-colors ${
                        location.pathname === item.path
                          ? 'bg-slate-50 text-[#0F2040] font-medium'
                          : 'text-slate-600 hover:bg-slate-50'
                      }`}
                      data-testid={`help-link-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <item.icon className="w-3.5 h-3.5" />
                      {item.label}
                    </button>
                  ))}
                  <div className="border-t border-slate-100 my-1" />
                  <button
                    onClick={() => { setHelpOpen(false); tourRestart(); }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
                    data-testid="help-take-tour"
                  >
                    <MapPin className="w-3.5 h-3.5" />
                    Take a Tour
                  </button>
                </div>
              )}
            </div>
            <TranscriptReadyPill />
            <NotificationBell />
            <div className="relative" ref={profileRef}>
            <button
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center gap-2 hover:opacity-90 transition-opacity"
              data-testid="profile-trigger"
            >
              <span className="text-xs text-slate-600 hidden md:inline">Hi, {user?.name?.split(' ')[0]}</span>
              {(() => { const rl = ROLE_LABELS[user?.role] || ROLE_LABELS.user; return (
                <span className={`hidden md:inline text-[10px] font-medium px-1.5 py-0.5 rounded-full ${rl.bg} ${rl.text}`} data-testid="role-badge">{rl.label}</span>
              ); })()}
              <UserAvatar user={user} size="sm" />
            </button>

            {profileOpen && (
              <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-slate-200 z-50" data-testid="profile-dropdown">
                {/* User info */}
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="font-semibold text-sm text-[#0F2040]">{user?.name}</p>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                  {user?.organization_name && (
                    <p className="text-xs text-[#4ABFCF] font-medium mt-0.5">{user.organization_name}</p>
                  )}
                </div>

                {/* Menu items */}
                <div className="py-1">
                  <button
                    onClick={() => navTo('/profile')}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
                    data-testid="profile-settings-link"
                  >
                    <User className="w-3.5 h-3.5" />
                    Profile Settings
                  </button>
                  <button
                    onClick={() => navTo('/profile?tab=communication')}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-xs text-slate-600 hover:bg-slate-50 transition-colors"
                    data-testid="notification-prefs-link"
                  >
                    <Bell className="w-3.5 h-3.5" />
                    Communication Preferences
                  </button>
                </div>

                {/* Sign out */}
                <div className="border-t border-slate-100 py-1">
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-4 py-2 text-xs text-red-500 hover:bg-red-50 transition-colors"
                    data-testid="sign-out-btn"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Row 2: Grouped navigation (Library · Workflow · Artifacts) */}
        <nav className="border-t border-slate-100" data-testid="nav-bar">
          {/* Mobile: hamburger drawer (one-handed) */}
          <div className="sm:hidden w-full px-4 flex items-center h-10">
            <button onClick={() => setMobileNavOpen(o => !o)}
              className="flex items-center gap-2 text-xs font-medium text-slate-600"
              data-testid="mobile-nav-toggle">
              <Menu className="w-4 h-4" />
              Menu
              {importPending > 0 && (
                <span className="min-w-[16px] h-4 px-1 rounded-full bg-[#4ABFCF] text-white text-[9px] font-bold flex items-center justify-center">{importPending}</span>
              )}
            </button>
          </div>
          {mobileNavOpen && (
            <div className="sm:hidden border-t border-slate-100 bg-white px-4 py-3 space-y-3 shadow-lg" data-testid="mobile-nav-drawer">
              {NAV_GROUPS.map(group => {
                const items = group.items.filter(item => (item.roles || []).includes(user?.role || 'user'));
                if (!items.length) return null;
                return (
                  <div key={group.id}>
                    {!group.direct && <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">{group.label}</div>}
                    <div className="flex flex-col">
                      {items.map(item => (
                        <button key={item.path}
                          onClick={() => { setMobileNavOpen(false); navTo(item.path); }}
                          className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm text-left ${location.pathname.startsWith(item.path) ? 'bg-[#0F2040]/5 text-[#0F2040] font-semibold' : 'text-slate-600'}`}
                          data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                          <item.icon className="w-4 h-4" />
                          {item.label}
                          {item.path === '/rfp-library' && importPending > 0 && (
                            <span className="ml-auto min-w-[16px] h-4 px-1 rounded-full bg-[#4ABFCF] text-white text-[9px] font-bold flex items-center justify-center">{importPending}</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
              {isAdmin && (
                <button onClick={() => { setMobileNavOpen(false); navTo('/admin'); }}
                  className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm w-full text-left ${isOnAdminPage ? 'bg-purple-50 text-purple-700 font-semibold' : 'text-slate-600'}`}
                  data-testid="mobile-nav-admin">
                  <Shield className="w-4 h-4" />
                  Admin
                </button>
              )}
            </div>
          )}
          <div className="hidden sm:flex w-full px-4 sm:px-6 lg:px-8 items-center h-10 gap-3 overflow-x-auto">
            {NAV_GROUPS.map((group) => {
              const visibleItems = group.items.filter(item => (item.roles || []).includes(user?.role || 'user'));
              if (visibleItems.length === 0) return null;
              const isActive = (item) => location.pathname === item.path
                || location.pathname.startsWith(item.path + '/')
                || (item.path === '/quotes' && (location.pathname.startsWith('/estimator/quote') || location.pathname === '/compare-quotes'))
                || (item.path === '/workspaces' && location.pathname.startsWith('/workspace/'));
              if (group.direct) {
                const item = visibleItems[0];
                return (
                  <button key={group.id} onClick={() => navTo(item.path)}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                      isActive(item) ? 'bg-[#0F2040]/5 text-[#0F2040]' : 'text-slate-500 hover:text-[#0F2040] hover:bg-slate-50'
                    }`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                    <item.icon className="w-3.5 h-3.5" />
                    {item.label}
                  </button>
                );
              }
              const groupActive = visibleItems.some(isActive);
              const groupBadge = group.id === 'deals' ? importPending : 0;
              return (
                <DropdownMenu key={group.id}>
                  <DropdownMenuTrigger asChild>
                    <button
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                        groupActive ? 'bg-[#0F2040]/5 text-[#0F2040]' : 'text-slate-500 hover:text-[#0F2040] hover:bg-slate-50'
                      }`}
                      data-testid={`nav-group-${group.id}`}>
                      <group.icon className="w-3.5 h-3.5" />
                      {group.label}
                      {groupBadge > 0 && (
                        <span className="min-w-[16px] h-4 px-1 rounded-full bg-[#4ABFCF] text-white text-[9px] font-bold flex items-center justify-center"
                          data-testid="nav-deals-badge">{groupBadge}</span>
                      )}
                      <ChevronDown className="w-3 h-3 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="min-w-[190px]">
                    {visibleItems.map(item => (
                      <DropdownMenuItem key={item.path} onClick={() => navTo(item.path)}
                        className={`text-xs cursor-pointer ${isActive(item) ? 'bg-[#0F2040]/5 text-[#0F2040] font-semibold' : ''}`}
                        data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                        <item.icon className="w-3.5 h-3.5 mr-2" />
                        {item.label}
                        {item.path === '/rfp-library' && importPending > 0 && (
                          <span className="ml-auto min-w-[16px] h-4 px-1 rounded-full bg-[#4ABFCF] text-white text-[9px] font-bold flex items-center justify-center"
                            data-testid="nav-rfp-library-badge">{importPending}</span>
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            })}

            {/* Spacer pushes Admin to the right */}
            <div className="flex-1" />

            {/* Admin — single button (no dropdown). The /admin page has
                its own grouped sidebar so we don't duplicate nav here. */}
            {isAdmin && (
              <button
                onClick={() => navTo('/admin')}
                className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-all whitespace-nowrap ${
                  isOnAdminPage
                    ? 'bg-purple-50 text-purple-700'
                    : 'text-slate-500 hover:text-[#0F2040] hover:bg-slate-50'
                }`}
                data-testid="nav-admin"
              >
                <Shield className="w-3.5 h-3.5" />
                Admin
              </button>
            )}
          </div>
        </nav>
      </header>

      {/* ─── CONTENT ─────────────────────────────────────────────── */}
      <main>{children}</main>

      {/* ─── ONBOARDING TOUR ─────────────────────────────────────── */}
      <OnboardingTour active={tourActive} onComplete={tourComplete} />

      {/* ─── GLOBAL COMMAND PALETTE (⌘K / Ctrl+K) ────────────────── */}
      <GlobalCommandPalette />

      {/* ─── ⌘K DISCOVERY CALLOUT (3rd+ login, one-time) ─────────── */}
      <PaletteCallout />

      {/* ─── QUICK ESTIMATE DRAWER ────────────────────────────────── */}
      <QuickEstimateDrawer open={quickDrawerOpen} onClose={() => setQuickDrawerOpen(false)} />
    </div>
  );
}

export { UserAvatar };
